import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import useSeo from "@/hooks/useSeo";

const formSubmitAddr = "https://readdy.ai/api/form/da2k3grd3r93mn1k3ad0";

const channels = [
  { icon: "ri-mail-line", title: "邮箱联系", desc: "contact@xiuxiu-comic.example.com" },
  { icon: "ri-community-line", title: "投稿建议", desc: "欢迎推荐你心中的宝藏恋爱番" },
  { icon: "ri-refund-2-line", title: "合作洽谈", desc: "题材合作与内容共创欢迎沟通" },
];

export default function Contact() {
  const [formStatus, setFormStatus] = useState<{ type: "idle" | "success" | "error"; msg: string }>({ type: "idle", msg: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setFormStatus({ type: "success", msg: "感谢您的留言，我们会尽快与您联系！" });
      form.reset();
      return;
    }
    formData.delete("website_alt");

    setSubmitting(true);
    setFormStatus({ type: "idle", msg: "" });
    try {
      const res = await fetch(formSubmitAddr, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
      if (res.ok && parsed?.code === "OK") {
        setFormStatus({ type: "success", msg: "感谢您的留言，我们会尽快与您联系！" });
        form.reset();
      } else {
        setFormStatus({
          type: "error",
          msg: serverMsg && String(serverMsg).trim() ? String(serverMsg) : "提交失败，请稍后再试。",
        });
      }
    } catch {
      setFormStatus({ type: "error", msg: "网络异常，请检查后重试。" });
    } finally {
      setSubmitting(false);
    }
  };

  useSeo({
    title: "联系我们 - 羞羞动漫漫画 | 投稿、建议与合作",
    description:
      "与羞羞动漫漫画取得联系：欢迎投稿你喜欢的恋爱后宫题材漫画、提交使用建议或洽谈内容合作。我们期待听到你的声音。",
    keywords: "联系羞羞动漫漫画,羞羞动漫漫画投稿,恋爱漫画合作",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: "联系羞羞动漫漫画",
      inLanguage: "zh-CN",
      mainEntity: {
        "@type": "Organization",
        name: "羞羞动漫漫画",
        email: "contact@xiuxiu-comic.example.com",
      },
    },
  });

  const inputClass =
    "w-full mt-1.5 px-4 py-2.5 rounded-lg bg-background-100 border border-background-300 text-sm text-foreground-900 focus:border-primary-400 focus:ring-2 focus:ring-primary-200 outline-none transition-colors placeholder:text-foreground-400";

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <section className="pt-24 md:pt-32 max-w-7xl mx-auto px-4 md:px-8 pb-10">
          <nav aria-label="面包屑" className="flex items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">联系我们</span>
          </nav>

          <div className="mt-8">
            <p className="text-sm font-medium text-primary-600 tracking-widest">CONTACT US</p>
            <h1 className="mt-3 font-heading font-black text-3xl md:text-5xl text-foreground-950">联系我们</h1>
            <p className="mt-4 text-base text-foreground-600 max-w-2xl leading-relaxed">
              无论是想推荐喜欢的恋爱后宫题材漫画、反馈使用建议，还是洽谈内容合作，都欢迎通过下方表单联系我们。我们会在收到留言后尽快回复。
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {channels.map((c) => (
              <div key={c.title} className="rounded-2xl bg-background-100 border border-background-200/70 p-5 flex items-start gap-3">
                <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600 text-lg">
                  <i className={c.icon}></i>
                </span>
                <div>
                  <h2 className="font-heading font-semibold text-foreground-950">{c.title}</h2>
                  <p className="mt-1 text-sm text-foreground-600">{c.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
          <div className="rounded-3xl bg-background-100 border border-background-200/70 p-6 md:p-10">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
              <div className="lg:col-span-2">
                <h2 className="font-heading font-bold text-2xl text-foreground-950">给我们留言</h2>
                <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                  填写右侧表单，告诉我们你的想法。我们会认真阅读每一条留言，你的支持是我们坚持更新的动力。
                </p>
                <div className="mt-6 space-y-4">
                  <div className="flex items-center gap-3 text-sm text-foreground-700">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700"><i className="ri-time-line"></i></span>
                    每日更新，留言回复一般不超过 2 个工作日
                  </div>
                  <div className="flex items-center gap-3 text-sm text-foreground-700">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700"><i className="ri-shield-check-line"></i></span>
                    您的个人信息仅用于回复，严格保密
                  </div>
                  <div className="flex items-center gap-3 text-sm text-foreground-700">
                    <span className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700"><i className="ri-heart-3-line"></i></span>
                    期待你的作品推荐与宝贵建议
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} data-readdy-form="contact-form" className="lg:col-span-3 flex flex-col gap-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="text-sm font-medium text-foreground-800">您的称呼</label>
                    <input id="name" name="name" type="text" required placeholder="请输入您的称呼" className={inputClass} />
                  </div>
                  <div>
                    <label htmlFor="email" className="text-sm font-medium text-foreground-800">邮箱地址</label>
                    <input id="email" name="email" type="email" required placeholder="name@example.com" className={inputClass} />
                  </div>
                </div>
                <div>
                  <label htmlFor="subject" className="text-sm font-medium text-foreground-800">留言主题</label>
                  <select id="subject" name="subject" className={inputClass}>
                    <option value="作品推荐">作品推荐</option>
                    <option value="使用建议">使用建议</option>
                    <option value="内容合作">内容合作</option>
                    <option value="其他问题">其他问题</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="message" className="text-sm font-medium text-foreground-800">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="请写下你想说的话，最多 500 字"
                    className={`${inputClass} resize-none`}
                  ></textarea>
                  <p className="mt-1 text-right text-xs text-foreground-400">最多 500 字</p>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {formStatus.type === "success" && (
                  <div className="px-4 py-3 rounded-lg bg-secondary-100 text-secondary-900 text-sm flex items-center gap-2">
                    <i className="ri-checkbox-circle-line"></i>
                    {formStatus.msg}
                  </div>
                )}
                {formStatus.type === "error" && (
                  <div className="px-4 py-3 rounded-lg bg-primary-100 text-primary-800 text-sm flex items-start gap-2">
                    <i className="ri-error-warning-line mt-0.5"></i>
                    <span>{formStatus.msg}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-1 inline-flex items-center justify-center whitespace-nowrap px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      提交中…
                    </>
                  ) : (
                    <>
                      提交留言
                      <i className="ri-send-plane-line ml-2"></i>
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}